import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle, ArrowDownLeft, ArrowUpRight, Bot, Radio, Copy, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

// ---------------------------------------------------------------------------
// Admin → Herramientas → Sincronización Discord
// Panel de auditoría y test de la integración bidireccional con el bot.
// ---------------------------------------------------------------------------
export default function InventorySyncAdmin() {
    const [config, setConfig] = useState(null);
    const [events, setEvents] = useState([]);
    const [filter, setFilter] = useState(""); // "" | "inbound" | "outbound"
    const [loading, setLoading] = useState(true);
    const [pinging, setPinging] = useState(false);
    const [lastPing, setLastPing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [cfg, ev] = await Promise.all([
                api.get("/admin/inventory-sync/config").then((r) => r.data),
                api.get(`/admin/inventory-sync/events${filter ? `?direction=${filter}` : ""}`).then((r) => r.data),
            ]);
            setConfig(cfg);
            setEvents(ev);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error cargando sincronización");
        } finally { setLoading(false); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const ping = async () => {
        setPinging(true);
        try {
            const { data } = await api.post("/admin/inventory-sync/ping");
            setLastPing(data);
            if (data.ok) toast.success("Conexión con el bot OK");
            else toast.error(`Falló: ${data.reason || `HTTP ${data.http_status}`}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally { setPinging(false); }
    };

    const copyKey = async () => {
        try {
            // The full key is only in the .env; we copy the preview to help identify it.
            await navigator.clipboard.writeText(config?.api_key_preview || "");
            toast.success("Preview copiado (la key completa está en el .env del servidor)");
        } catch { toast.error("No se pudo copiar"); }
    };

    return (
        <div className="space-y-4" data-testid="inventory-sync-admin">
            {/* Intro */}
            <div className="tb-card p-4">
                <h3 className="font-display text-lg mb-1 inline-flex items-center gap-2">
                    <Bot className="w-4 h-4 text-[var(--tb-gold)]" /> Sincronización con Bot Discord
                </h3>
                <p className="text-xs text-[var(--tb-text-2)] leading-relaxed">
                    Sincronización bidireccional del inventario entre tu bot de Discord y este sitio. Cuando se vende un
                    producto en Discord, se descuenta aquí. Cuando se vende aquí (SV), se descuenta en Discord.
                    La coincidencia se hace por SKU (`T-0001`, `T-0002`...).
                </p>
            </div>

            {/* Config status */}
            <div className="tb-card p-4">
                <h4 className="font-display mb-3">Estado de la conexión</h4>
                {loading || !config ? (
                    <div className="text-xs text-[var(--tb-muted)]">Cargando…</div>
                ) : (
                    <div className="space-y-2 text-sm">
                        <ConfigRow
                            label="API Key configurada"
                            ok={config.api_key_configured}
                            value={config.api_key_preview || "(no configurada)"}
                            onCopy={config.api_key_configured ? copyKey : null}
                        />
                        <ConfigRow
                            label="URL del bot (Reverse Proxy)"
                            ok={!!config.bot_url}
                            value={config.bot_url || "(no configurada — Web → Discord deshabilitado)"}
                        />
                        <ConfigRow
                            label="Envío de eventos hacia el bot"
                            ok={config.enabled}
                            value={config.enabled ? "Activado" : "Desactivado"}
                        />
                        <div className="pt-2 flex items-center gap-2 flex-wrap">
                            <button onClick={ping} disabled={pinging || !config.bot_url}
                                className="tb-btn-primary !text-xs inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid="inv-sync-ping">
                                <Radio className={`w-3.5 h-3.5 ${pinging ? "animate-pulse" : ""}`} />
                                {pinging ? "Enviando ping…" : "Probar conexión con el bot"}
                            </button>
                            <button onClick={load} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid="inv-sync-refresh">
                                <RefreshCw className="w-3.5 h-3.5" /> Refrescar
                            </button>
                        </div>
                        {lastPing && (
                            <div className={`mt-2 text-[11px] px-3 py-2 rounded border ${lastPing.ok ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : "border-rose-400/40 bg-rose-500/10 text-rose-300"}`}>
                                {lastPing.ok
                                    ? <>✅ Ping OK (HTTP {lastPing.http_status})</>
                                    : <>❌ {lastPing.reason || `HTTP ${lastPing.http_status}`}</>}
                            </div>
                        )}
                        {!config.bot_url && (
                            <div className="mt-3 text-[11px] p-3 rounded border border-amber-400/40 bg-amber-500/10 text-amber-300 inline-flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <div>
                                    Falta configurar <code>DISCORD_BOT_WEBHOOK_URL</code> en el <code>.env</code> del backend
                                    (ej. <code>https://discord-bot.shopterraboutique.com</code>). Sin esto, los eventos
                                    Web → Discord no se envían. Discord → Web ya funciona.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Events audit */}
            <div className="tb-card p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="font-display">Historial de eventos ({events.length})</h4>
                    <div className="flex items-center gap-1">
                        {[
                            { id: "", label: "Todos" },
                            { id: "inbound", label: "Discord → Web" },
                            { id: "outbound", label: "Web → Discord" },
                        ].map((f) => (
                            <button key={f.id} onClick={() => setFilter(f.id)}
                                className={`text-[11px] px-2 py-1 rounded border ${filter === f.id ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10 text-[var(--tb-gold)]" : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/50"}`}
                                data-testid={`inv-sync-filter-${f.id || "all"}`}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                {loading ? (
                    <div className="text-xs text-[var(--tb-muted)] text-center py-6">Cargando…</div>
                ) : events.length === 0 ? (
                    <div className="text-xs text-[var(--tb-muted)] text-center py-6">Aún no hay eventos registrados.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase text-[var(--tb-muted)] border-b border-[var(--tb-border)]">
                                    <th className="p-2 text-left">Cuándo</th>
                                    <th className="p-2 text-left">Dirección</th>
                                    <th className="p-2 text-left">Evento</th>
                                    <th className="p-2 text-left">SKU</th>
                                    <th className="p-2 text-right">Cantidad</th>
                                    <th className="p-2 text-center">Estado</th>
                                    <th className="p-2 text-left">Detalle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((e) => <EventRow key={e.id} e={e} />)}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function ConfigRow({ label, ok, value, onCopy }) {
    return (
        <div className="flex items-center gap-2 flex-wrap">
            {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />}
            <span className="text-[var(--tb-text-2)]">{label}:</span>
            <code className="text-[11px] bg-[var(--tb-bg-2)] px-1.5 py-0.5 rounded flex-1 min-w-0 truncate">{value}</code>
            {onCopy && (
                <button onClick={onCopy} className="tb-btn-ghost !p-1" title="Copiar">
                    <Copy className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}

function EventRow({ e }) {
    const status = e.status || "unknown";
    const badge = {
        success: "bg-emerald-500/20 text-emerald-300",
        duplicate: "bg-slate-500/20 text-slate-300",
        skipped: "bg-amber-500/20 text-amber-300",
        error: "bg-rose-500/20 text-rose-300",
    }[status] || "bg-[var(--tb-bg-2)] text-[var(--tb-muted)]";
    const dt = e.created_at ? new Date(e.created_at) : null;
    return (
        <tr className="border-b border-[var(--tb-border)] hover:bg-[var(--tb-bg-2)]/30">
            <td className="p-2 text-[11px] whitespace-nowrap">
                {dt ? dt.toLocaleString("es-SV", { hour12: false }) : "—"}
            </td>
            <td className="p-2">
                {e.direction === "inbound"
                    ? <span className="inline-flex items-center gap-1 text-[11px] text-cyan-300"><ArrowDownLeft className="w-3 h-3" /> Discord → Web</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] text-fuchsia-300"><ArrowUpRight className="w-3 h-3" /> Web → Discord</span>}
            </td>
            <td className="p-2 font-mono">{e.event}</td>
            <td className="p-2 font-mono">{e.sku || "—"}</td>
            <td className="p-2 text-right">{e.quantity || 0}</td>
            <td className="p-2 text-center">
                <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${badge}`}>
                    {status}
                </span>
            </td>
            <td className="p-2 text-[11px] text-[var(--tb-text-2)] max-w-[220px] truncate" title={e.error || JSON.stringify(e.result || {})}>
                {e.error || (e.result?.reason) || (e.result?.new_stock_sv !== undefined ? `stock SV: ${e.result.new_stock_sv}` : "—")}
            </td>
        </tr>
    );
}
